// All business rules centralised here

export const RUPEE = 1 // keep as 1 for logic, treat numeric amounts directly in INR
export const TARGET_EMPLOYEE = 10 * 100000 // 10 lakhs
export const TARGET_MANAGER = 60 * 100000 // 60 lakhs

export function calcDiscountPct(listPrice, saleValue){
  if (!listPrice || !saleValue) return 0
  const pct = ((listPrice - saleValue) / listPrice) * 100
  return Math.max(0, Math.round(pct * 100) / 100)
}

export function discountIncentiveRate(discountPct){
  if (discountPct > 25) return 0 // invalid by policy; UI blocks earlier
  if (discountPct > 20 && discountPct <= 25) return 0.0015
  if (discountPct > 15 && discountPct <= 20) return 0.003
  if (discountPct > 10 && discountPct <= 15) return 0.0045
  if (discountPct > 5 && discountPct <= 10)  return 0.006
  // <= 5%
  return 0.0075
}

export function ticketIncentiveRate(amount){
  const a = amount
  if (a < 300000) return 0
  if (a >= 300000 && a < 400000) return 0.00075
  if (a >= 400000 && a < 500000) return 0.0015
  if (a >= 500000 && a < 600000) return 0.00225
  if (a >= 600000 && a < 700000) return 0.003
  return 0.00375 // >= 7 lakhs
}

export function monthlyIncentiveRate(achievementPct){
  if (achievementPct >= 120) return 0.0075
  if (achievementPct >= 110) return 0.006
  if (achievementPct >= 100) return 0.0045
  if (achievementPct >= 90)  return 0.003
  if (achievementPct >= 80)  return 0.0015
  return 0
}

export function monthlyGatePassed(totalMonthSales, target){
  const ach = (totalMonthSales / target) * 100
  return ach >= 80
}

export function computeEmployeeMonthSummary(salesRecords){
  const total = salesRecords.reduce((s, r) => s + r.saleValue, 0)
  const achPct = (total / TARGET_EMPLOYEE) * 100
  const monthRate = monthlyIncentiveRate(achPct)
  const gate = achPct >= 80

  let discountIncent = 0
  let ticketIncent = 0

  salesRecords.forEach(r => {
    const dRate = discountIncentiveRate(r.discountPct)
    const tRate = ticketIncentiveRate(r.saleValue)
    // Apply gate: if gate fails, 0 for all categories
    discountIncent += gate ? r.saleValue * dRate : 0
    ticketIncent   += gate ? r.saleValue * tRate : 0
  })

  const monthlyIncent = gate ? total * monthRate : 0
  const grandTotal = discountIncent + ticketIncent + monthlyIncent

  return { totalSales: total, achPct, monthRate, gate, discountIncent, ticketIncent, monthlyIncent, grandTotal }
}

// Manager summary uses branch-wide sales (including manager's own)
export function computeManagerMonthSummary(branchSalesRecords, expensesAmount){
  const total = branchSalesRecords.reduce((s, r) => s + r.saleValue, 0)
  const achPct = (total / TARGET_MANAGER) * 100
  const monthRate = monthlyIncentiveRate(achPct)
  const gate = achPct >= 80

  // Even for manager, per-order categories exist but apply gate
  let discountIncent = 0
  let ticketIncent = 0
  branchSalesRecords.forEach(r => {
    const dRate = discountIncentiveRate(r.discountPct)
    const tRate = ticketIncentRateSafe(r.saleValue)
    discountIncent += gate ? r.saleValue * dRate : 0
    ticketIncent   += gate ? r.saleValue * tRate : 0
  })

  const monthlyIncent = gate ? total * monthRate : 0

  // Expenses bonus: only if gate passed and expenses <= 2.5% of monthly sales
  let expenseBonus = 0
  if (gate && total > 0) {
    const expPct = (expensesAmount / total) * 100
    if (expPct <= 2.5) {
      expenseBonus = total * 0.025
    }
  }

  const grandTotal = discountIncent + ticketIncent + monthlyIncent + expenseBonus
  return { totalSales: total, achPct, monthRate, gate, discountIncent, ticketIncent, monthlyIncent, expenseBonus, grandTotal }
}

function ticketIncentRateSafe(a){
  return ticketIncentiveRate(a)
}

export function formatINR(n){
  if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
  }
  return `₹${(n||0).toFixed(0)}`
}